import { useEffect } from 'react';
import { create } from 'zustand';
import { useTranslation } from 'react-i18next';
import { Icon } from './index';

/**
 * Toast (03-design-system § 4.15): a short confirmation bottom-right, 4 s,
 * tones neutral / success / danger. 10-web-admin § 20 sends `403` and `429`
 * here as well.
 *
 * A store rather than a context so a mutation's `onSuccess` can call `toast()`
 * without being rendered under a provider — the publish screen navigates away
 * in the same tick, and a context would have unmounted with it.
 */

export type ToastTone = 'neutral' | 'success' | 'danger';

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  /** One optional action, e.g. "Aufkleber drucken" after a publish (§ 7). */
  action?: { label: string; onPress: () => void };
}

interface ToastState {
  items: ToastItem[];
  push: (item: Omit<ToastItem, 'id'>) => void;
  dismiss: (id: number) => void;
}

const DURATION_MS = 4_000;
let nextId = 1;

export const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (item) => {
    const id = nextId++;
    set((state) => ({ items: [...state.items, { ...item, id }] }));
  },
  dismiss: (id) => set((state) => ({ items: state.items.filter((item) => item.id !== id) })),
}));

export function toast(
  message: string,
  options: { tone?: ToastTone; action?: ToastItem['action'] } = {},
): void {
  useToastStore.getState().push({ message, tone: options.tone ?? 'neutral', action: options.action });
}

const TONE_CLASS: Record<ToastTone, string> = {
  neutral: 'bg-ink text-white',
  success: 'bg-success-bg text-success-text',
  danger: 'bg-danger-bg text-danger-text',
};

export function Toaster() {
  const items = useToastStore((state) => state.items);
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-6 right-6 z-[60] flex w-[360px] max-w-[calc(100vw-48px)] flex-col gap-2"
    >
      {items.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
    </div>
  );
}

function ToastView({ item }: { item: ToastItem }) {
  const { t } = useTranslation();
  const dismiss = useToastStore((state) => state.dismiss);

  // A toast with an action stays until it is answered or closed: four seconds
  // are too short to read "Version 5 ist live." and decide about stickers.
  useEffect(() => {
    if (item.action) return;
    const timer = window.setTimeout(() => dismiss(item.id), DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [dismiss, item]);

  return (
    <div
      role="status"
      className={`rounded-card pointer-events-auto flex items-center gap-3 px-4 py-3 shadow-lg ${TONE_CLASS[item.tone]}`}
    >
      <p className="font-body flex-1 text-sm font-semibold">{item.message}</p>
      {item.action ? (
        <button
          type="button"
          onClick={() => {
            dismiss(item.id);
            item.action?.onPress();
          }}
          className="font-body rounded-input bg-white/15 px-2.5 py-1 text-sm font-semibold hover:bg-white/25"
        >
          {item.action.label}
        </button>
      ) : null}
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={() => dismiss(item.id)}
        className="flex h-7 w-7 flex-none items-center justify-center rounded-full opacity-70 hover:opacity-100"
      >
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}
